import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { Trans } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { BrandLoadingScreen } from '~/components/brand/brand-loader'
import { PublicMessage, PublicShell } from '~/components/funnel/public-shell'
import { PortalView } from '~/components/portal/portal-view'
import { capture, setWorkspaceGroup } from '~/lib/posthog'
import { optionalBoolean } from '~/lib/search-params'
import { api } from '~convex/_generated/api'

const RouteComponent = () => {
  const { portalSlug, workspaceSlug } = Route.useParams()
  const { preview } = Route.useSearch()
  const { data: payload, isPending } = useQuery(
    convexQuery(api.portal.getBySlug, { portalSlug, workspaceSlug })
  )
  const tracked = useRef(false)
  const recordView = useConvexMutation(api.portal.recordView)

  // Anonymous — this is our customer's client looking at their own project.
  // The URL carries the slug, but the group key is the id, so this reads it
  // off the payload like `/b` and `/p` do rather than trusting the param.
  useEffect(() => {
    if (payload === undefined || tracked.current) {
      return
    }
    tracked.current = true
    if (payload.status === 'active') {
      setWorkspaceGroup(payload.workspaceId)
      // Fire-and-forget: the studio's portal tab answers "has the client
      // looked?" off this stamp. The studio's own Open button carries an
      // explicit preview flag so it cannot manufacture a client receipt.
      if (preview !== true) {
        recordView({ portalSlug, workspaceSlug }).catch(() => undefined)
      }
    }
    if (preview !== true) {
      capture('client_link_opened', { kind: 'portal', status: payload.status })
    }
  }, [payload, portalSlug, preview, recordView, workspaceSlug])

  if (isPending || payload === undefined) {
    return <BrandLoadingScreen />
  }

  if (payload.status === 'not_found') {
    return (
      <PublicShell>
        <PublicMessage
          body={<Trans>This portal link is not valid. Ask your architect for a new one.</Trans>}
          title={<Trans>Link not found</Trans>}
        />
      </PublicShell>
    )
  }

  return (
    <PublicShell width="wide">
      <PortalView payload={payload} />
    </PublicShell>
  )
}

export const Route = createFileRoute('/c/$workspaceSlug/$portalSlug')({
  component: RouteComponent,
  // The portal slug is the capability: anyone holding the URL sees a real
  // client's specification, prices and decisions. It must never become a
  // search result, and clicking through to a store must not carry the path
  // out in a Referer header. robots.txt is only advisory, so the document
  // says it too — same policy as `/b` and `/p`.
  head: () => ({
    meta: [
      { content: 'noindex, nofollow', name: 'robots' },
      { content: 'no-referrer', name: 'referrer' },
    ],
  }),
  // Public unauthenticated surface — runs entirely against the Convex client.
  ssr: false,
  validateSearch: (search: Record<string, unknown>): { preview?: boolean } => ({
    preview: optionalBoolean(search.preview),
  }),
})
